import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Workflow } from 'lucide-react'
import { z } from 'zod'

import { WorkflowEditor } from '@/components/settings/workflows/workflow-editor'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { SkeletonBar } from '@/components/shared/skeleton-bar'
import { useGetWorkflowDefinition } from '@/stores/workflows.store'

const editorTabSchema = z.object({
  tab: z.enum(['editor', 'runs', 'settings']).optional(),
})

export const Route = createFileRoute('/_app/settings_/workflows/$id/edit')({
  validateSearch: editorTabSchema,
  component: EditWorkflowPage,
})

function EditWorkflowPage() {
  const { id } = Route.useParams()
  const { tab } = Route.useSearch()
  const navigate = useNavigate()
  const { workflowDefinition, isLoading, isError } = useGetWorkflowDefinition(id)

  if (isLoading) {
    return <WorkflowEditorSkeleton />
  }

  if (isError || !workflowDefinition) {
    return (
      <div className="flex flex-col gap-4 p-6">
        <Empty className="flex-1 border border-dashed py-12">
          <EmptyMedia variant="icon">
            <Workflow className="h-6 w-6" />
          </EmptyMedia>
          <EmptyDescription>We couldn't load this workflow right now.</EmptyDescription>
        </Empty>
      </div>
    )
  }

  return (
    <WorkflowEditor
      organizationId={workflowDefinition.organizationId}
      definition={workflowDefinition}
      activeTab={tab ?? 'editor'}
      onTabChange={(next) => navigate({ to: '.', search: { tab: next } })}
    />
  )
}

/**
 * Placeholder that mirrors the full-page editor chrome (top bar + canvas area)
 * while the definition loads, so the takeover lands without a layout jump.
 */
function WorkflowEditorSkeleton() {
  return (
    <div
      className="bg-background fixed inset-0 z-40 flex h-dvh flex-col"
      aria-busy="true"
      aria-label="Loading workflow editor"
    >
      {/* Top bar */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b px-4">
        <div className="flex items-center gap-3">
          <SkeletonBar className="h-8 w-8 rounded-md" />
          <SkeletonBar className="h-5 w-48" />
        </div>
        <SkeletonBar className="h-8 w-56 rounded-md" />
        <div className="flex items-center gap-2">
          <SkeletonBar className="h-8 w-24 rounded-md" />
          <SkeletonBar className="h-8 w-28 rounded-md" />
        </div>
      </div>
      {/* Canvas placeholder */}
      <div className="min-h-0 flex-1 p-6">
        <SkeletonBar className="h-full w-full rounded-xl" />
      </div>
    </div>
  )
}
